import { ListFilter, RefreshCw } from "lucide-react";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { FilterChip } from "@/components/ui/filter-chip";
import {
  ListSearch,
  ListToolbar,
  SortMenu
} from "@/components/ui/list-toolbar";

import { CaseStatus } from "../../../api/types";
import { VISAVALUE } from "../../../types/visa.type";
import type { CaseChipCounts } from "../casesChips";

export const initialsFromLabel = (label: string): string => {
  const parts = label.split(" ").filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  const out = `${first}${last}`.toUpperCase();
  return out || "U";
};

export type SortDir = "asc" | "desc";

const CASES_SORT_OPTIONS: Array<{
  sortBy: string;
  sortDir: SortDir;
  label: string;
}> = [
  { sortBy: "", sortDir: "desc", label: "Default" },
  { sortBy: "created_at", sortDir: "desc", label: "Date Created (Newest)" },
  { sortBy: "created_at", sortDir: "asc", label: "Date Created (Oldest)" },
  { sortBy: "first_name", sortDir: "asc", label: "First Name (A–Z)" },
  { sortBy: "first_name", sortDir: "desc", label: "First Name (Z–A)" },
  { sortBy: "last_name", sortDir: "asc", label: "Last Name (A–Z)" },
  { sortBy: "last_name", sortDir: "desc", label: "Last Name (Z–A)" },
  { sortBy: "status", sortDir: "asc", label: "Status (A–Z)" },
  { sortBy: "visa", sortDir: "asc", label: "Visa (A–Z)" }
];

const sortKey = (sortBy: string, sortDir: SortDir) =>
  sortBy ? `${sortBy}:${sortDir}` : "";

export type PracticeTeamMember = {
  uid?: string;
  label: string;
  email?: string;
};

interface CasesTableToolbarProps {
  searchInput: string;
  onSearchChange: (value: string) => void;
  statusFilter: CaseStatus | "";
  onStatusChange: (value: CaseStatus | "") => void;
  visaFilter: string;
  onVisaChange: (value: string) => void;
  attorneyFilter: string;
  onAttorneyChange: (value: string) => void;
  practiceTeamMemberOptions: PracticeTeamMember[];
  /** The signed-in user; enables the Mine chip. */
  currentUserId?: string | null;
  /** All / Mine / top visa chips; null until the first unfiltered page loads. */
  chips: CaseChipCounts | null;
  sortBy: string;
  sortDir: SortDir;
  onSortChange: (sortBy: string, sortDir: SortDir) => void;
  onRefresh: () => void;
}

type RadioSubProps = {
  label: string;
  anyLabel: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onValueChange: (value: string) => void;
  scroll?: boolean;
};

const RadioSub = ({
  label,
  anyLabel,
  value,
  options,
  onValueChange,
  scroll = false
}: RadioSubProps) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger>
      <span className="flex-1">{label}</span>
      {value ? (
        <span className="max-w-28 truncate text-fg-3">
          {options.find((o) => o.value === value)?.label ?? value}
        </span>
      ) : null}
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent className="w-60">
      <DropdownMenuRadioGroup value={value} onValueChange={onValueChange}>
        <DropdownMenuRadioItem value="">{anyLabel}</DropdownMenuRadioItem>
        <DropdownMenuSeparator />
        <div className={scroll ? "max-h-60 overflow-y-auto" : undefined}>
          {options.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value}>
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </div>
      </DropdownMenuRadioGroup>
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);

/**
 * The cases `ListPage` toolbar: search, the All / Mine / visa quick filters
 * with their counts, then Filter (status, any visa type, any assignee), sort
 * and refresh on the right. Filters set from the menu that no chip shows
 * appear as clearable chips.
 */
export const CasesTableToolbar = ({
  searchInput,
  onSearchChange,
  statusFilter,
  onStatusChange,
  visaFilter,
  onVisaChange,
  attorneyFilter,
  onAttorneyChange,
  practiceTeamMemberOptions,
  currentUserId,
  chips,
  sortBy,
  sortDir,
  onSortChange,
  onRefresh
}: CasesTableToolbarProps) => {
  const visaOptions = useMemo(
    () =>
      Object.values(VISAVALUE)
        .filter((value) => value !== VISAVALUE.EMPTY)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value })),
    []
  );
  const memberOptions = useMemo(
    () =>
      practiceTeamMemberOptions.flatMap((m) => {
        const uid = (m.uid ?? "").trim();
        return uid ? [{ value: uid, label: m.label }] : [];
      }),
    [practiceTeamMemberOptions]
  );

  const me = (currentUserId ?? "").trim();
  const attorney = attorneyFilter.trim();
  const mineActive = Boolean(me) && attorney === me;
  const visaChips = chips?.visas ?? [];
  const visaHasChip = visaChips.some((v) => v.visa === visaFilter);
  const otherAttorney =
    attorney && !mineActive
      ? memberOptions.find((m) => m.value === attorney)?.label ?? "Someone"
      : null;
  const menuCount =
    (statusFilter ? 1 : 0) + (visaFilter ? 1 : 0) + (attorney ? 1 : 0);

  return (
    <ListToolbar>
      <ListSearch
        value={searchInput}
        onValueChange={onSearchChange}
        placeholder="Search cases…"
        aria-label="Search Cases"
        className="w-50"
      />
      <div
        role="group"
        aria-label="Quick Filters"
        className="flex min-w-0 flex-wrap items-center gap-1.5"
      >
        <FilterChip
          active={!visaFilter && !attorney}
          count={chips?.all}
          onClick={() => {
            onVisaChange("");
            onAttorneyChange("");
          }}
          data-testid="cases-chip-all"
        >
          All
        </FilterChip>
        {me ? (
          <FilterChip
            active={mineActive}
            count={chips?.mine ?? undefined}
            onClick={() => onAttorneyChange(mineActive ? "" : me)}
            data-testid="cases-chip-mine"
          >
            Mine
          </FilterChip>
        ) : null}
        {visaChips.map(({ visa, count }) => (
          <FilterChip
            key={visa}
            active={visaFilter === visa}
            count={count ?? undefined}
            onClick={() => onVisaChange(visaFilter === visa ? "" : visa)}
            data-testid={`cases-chip-visa-${visa}`}
          >
            {visa}
          </FilterChip>
        ))}
        {statusFilter ? (
          <FilterChip active onClear={() => onStatusChange("")}>
            Status: {statusFilter}
          </FilterChip>
        ) : null}
        {visaFilter && !visaHasChip ? (
          <FilterChip active onClear={() => onVisaChange("")}>
            Visa Type: {visaFilter}
          </FilterChip>
        ) : null}
        {otherAttorney ? (
          <FilterChip active onClear={() => onAttorneyChange("")}>
            Assigned: {otherAttorney}
          </FilterChip>
        ) : null}
      </div>
      <span className="flex-1" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Filter">
            <ListFilter />
            <span className="hidden md:inline">Filter</span>
            {menuCount ? (
              <span className="text-fg-4 tabular-nums">{menuCount}</span>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <RadioSub
            label="Status"
            anyLabel="Any Status"
            value={statusFilter}
            options={Object.values(CaseStatus).map((s) => ({
              value: s,
              label: s
            }))}
            onValueChange={(v) => onStatusChange(v as CaseStatus | "")}
          />
          <RadioSub
            label="Visa Type"
            anyLabel="Any Visa Type"
            value={visaFilter}
            options={visaOptions}
            onValueChange={onVisaChange}
            scroll
          />
          {memberOptions.length ? (
            <RadioSub
              label="Assigned"
              anyLabel="Anyone"
              value={attorney}
              options={memberOptions}
              onValueChange={onAttorneyChange}
              scroll
            />
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <SortMenu
        options={CASES_SORT_OPTIONS.map((o) => ({
          value: sortKey(o.sortBy, o.sortDir),
          label: o.label
        }))}
        value={sortKey(sortBy, sortDir)}
        onValueChange={(key) => {
          const picked = CASES_SORT_OPTIONS.find(
            (o) => sortKey(o.sortBy, o.sortDir) === key
          );
          if (picked) onSortChange(picked.sortBy, picked.sortDir);
        }}
      />
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Refresh"
        onClick={onRefresh}
      >
        <RefreshCw />
      </Button>
    </ListToolbar>
  );
};
